import React from 'react';
import { motion } from 'framer-motion';

const sectionTitleClass = 'text-2xl md:text-3xl font-semibold text-slate-900 dark:text-white';
const paragraphClass = 'text-base md:text-lg text-slate-600 dark:text-slate-300 leading-relaxed';

const principles = [
  {
    title: 'Scope & Engagement',
    content:
      'These terms apply when you access our websites, request proposals, or engage FirstLogic Technologies for consulting and engineering services.'
  },
  {
    title: 'Intellectual Property',
    content:
      'You retain ownership of your data and confidential materials. Deliverables produced under a statement of work follow the IP model defined in that agreement.'
  },
  {
    title: 'Acceptable Use',
    content:
      'You agree not to misuse our platforms, attempt to gain unauthorized access, or violate applicable laws while collaborating with our teams.'
  }
];

const commitments = [
  {
    title: 'Service Delivery',
    bullets: [
      'We provide experienced squads aligned to the scope defined in mutually executed contracts.',
      'We may use trusted affiliates or subcontractors but remain responsible for their work.',
      'Timelines depend on your team providing timely feedback, access, and approvals.'
    ]
  },
  {
    title: 'Confidentiality & Security',
    bullets: [
      'Each party must protect confidential information using industry-standard safeguards.',
      'Either party may disclose information if required by law, after providing notice when permitted.',
      'Security incidents are handled under our documented incident response procedures.'
    ]
  },
  {
    title: 'Payment & Invoicing',
    bullets: [
      'Services are invoiced per the schedule defined in the statement of work.',
      'Late payments may accrue interest or pause delivery after notice.',
      'Expenses are billed at cost with prior approval when required.'
    ]
  }
];

const legal = [
  'Both parties represent that they have authority to enter into the agreement.',
  'Liability is limited to the amounts paid in the twelve months preceding a claim, excluding fraud or gross negligence.',
  'Either party may terminate for material breach with 30 days’ notice if the issue is not cured.',
  'Governing law and venue are the state and federal courts of Texas, unless otherwise stated in a statement of work.'
];

const heroImage = 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1200&q=60';

export const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* Hero Banner Section */}
      <section className="relative isolate overflow-hidden pt-36 pb-32">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Terms of Service"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/65 via-slate-950/55 to-slate-950/65" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white drop-shadow-[0_4px_20px_rgba(15,23,42,0.4)]">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 dark:bg-slate-900/40 backdrop-blur-sm border border-white/40 dark:border-slate-700 text-sm font-semibold mb-4"
          >
            Terms of Service
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white"
          >
            Clear expectations for modern engineering partnerships
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg md:text-xl text-white/85 leading-relaxed"
          >
            Please review these terms carefully before using our websites or engaging our teams. By
            continuing, you agree to the principles described below.
          </motion.p>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-8 md:grid-cols-3">
          {principles.map((principle) => (
            <motion.div
              key={principle.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 p-6 shadow-sm"
            >
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                {principle.title}
              </h2>
              <p className={paragraphClass}>{principle.content}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {commitments.map((commitment, idx) => (
            <motion.div
              key={commitment.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 p-8 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-300">
                  {`Section ${idx + 1}`}
                </span>
                <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
              </div>
              <h2 className={`${sectionTitleClass} mt-3`}>{commitment.title}</h2>
              <ul className="mt-6 space-y-3">
                {commitment.bullets.map((bullet) => (
                  <li key={bullet} className="flex gap-3 text-slate-600 dark:text-slate-300">
                    <span className="mt-2 h-2 w-2 rounded-full bg-blue-500 inline-block" />
                    <span className="flex-1">{bullet}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-blue-50 dark:bg-slate-900/70 p-8 shadow-sm"
          >
            <h2 className={sectionTitleClass}>Legal Notes</h2>
            <ul className="mt-6 space-y-3">
              {legal.map((item) => (
                <li key={item} className="flex gap-3 text-slate-700 dark:text-slate-200">
                  <span className="mt-2 h-2 w-2 rounded-full bg-blue-500 inline-block" />
                  <span className="flex-1">{item}</span>
                </li>
              ))}
            </ul>
            <p className={`mt-6 ${paragraphClass}`}>
              Questions? Email legal@firstlogic.tech. We may revise these terms periodically and
              will post the latest version with an updated effective date.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfServicePage;

